import { Nullable } from '@main/shared/types';
import { FilterMode } from '@main/shared/url-helpers';
import { createSorter, type Sortable } from '@main/shared/utils';
import { Column, ColumnDef, Row, SortDirection, Table } from '@tanstack/react-table';
import { OnChangeValue } from 'chakra-react-select';
import { ReactElement } from 'react';

import { ComboboxProps, selectValueToArray } from '../../../combobox';
import { TableColumnType } from '../../table';
import { CellContainer } from '../cell';
import { createSelectFilter } from './select-filter';

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export type OptionHelperConfig<TOptionBase, TExtraColumnDef> = {
  columnType: Extract<TableColumnType, 'avatar' | 'status' | 'tag' | 'relation'>;
  getOptionValue: (option: TOptionBase) => string;
  getOptionLabel: (option: TOptionBase) => string;

  components: {
    Preview: <TData, TOption extends TOptionBase, IsMulti extends boolean, TFilter, TSort>(props: {
      columnDef: OptionColumnDef<TData, TOption, IsMulti, TFilter, TSort> & TExtraColumnDef;
      value?: OnChangeValue<TOption, IsMulti>;
    }) => ReactElement;

    /**
     * Combobox used by the column filter UI only.
     * Cell editing is layered on by `createSelectHelper`, not this helper.
     */
    Edit: <TOption extends TOptionBase, IsMulti extends boolean>(
      props: Partial<ComboboxProps<TOption, IsMulti>>,
    ) => ReactElement;
  };
};

export type OptionColumnDef<TData, TOption, IsMulti extends boolean, TFilter, TSort> = ColumnDef<
  TData,
  OnChangeValue<TOption, IsMulti> | undefined
> &
  OptionColumnConfig<TData, TOption, IsMulti, TFilter, TSort>;

export type OptionColumnConfig<TData, TOption, IsMulti extends boolean, TFilter, TSort> = {
  header: string;
  isMulti?: IsMulti;
  /**
   * Should return an array of possible select options.
   * When not defined, the options will be inferred from the table data.
   */
  getOptions?: (table: Table<TData>, column: Column<TData>) => TOption[] | Promise<TOption[]>;
  getGlobalFilterCondition?(filterValue: string): Nullable<TFilter>;
  getColumnFilterCondition?(filterMode: FilterMode, filterValue: string[]): Nullable<TFilter>;
  getColumnSort?(sortDirection: SortDirection): TSort;
};

/**
 * Read-only multi-value option column helper.
 * Shared by editable select columns (`createSelectHelper`) and read-only
 * relation columns.
 */
export function createOptionColumnHelper<TOptionBase extends object, TExtraColumnDef = object>({
  columnType,
  getOptionValue,
  getOptionLabel,
  components,
}: OptionHelperConfig<TOptionBase, TExtraColumnDef>) {
  return <TData, TFilter, TSort>() =>
    <TOption extends TOptionBase, IsMulti extends boolean = false>(
      columnDef: OptionColumnDef<TData, TOption, IsMulti, TFilter, TSort> & TExtraColumnDef,
    ): ColumnDef<TData, OnChangeValue<TOption, IsMulti> | undefined> => {
      const selectFilter = createSelectFilter<TData, TOptionBase, TOption, IsMulti>({
        name: columnDef.header,
        getOptions: columnDef.getOptions,
        getOptionValue,
        getOptionLabel,
        components,
      });

      const sorter = createSorter();
      const getSortableOrder = (option?: TOption): Sortable['order'] => {
        if (!option) return undefined;
        const maybeSortable = option as unknown as Sortable;
        const order = maybeSortable.order;
        return order === null || order === undefined ? undefined : order;
      };

      return {
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        sortDescFirst: false,
        sortUndefined: false,

        ...columnDef,

        cell: ({ getValue, cell }) => (
          <CellContainer p={0} cell={cell}>
            <components.Preview columnDef={columnDef} value={getValue()} />
          </CellContainer>
        ),

        filterFn: selectFilter.filterFn,

        sortingFn(rowA, rowB, columnId) {
          const getFirstOption = (row: Row<TData>): TOption | undefined => {
            const [option] = selectValueToArray<TOption>(row.getValue(columnId));
            return option;
          };

          const optionA = getFirstOption(rowA);
          const optionB = getFirstOption(rowB);

          const orderA = getSortableOrder(optionA);
          const orderB = getSortableOrder(optionB);

          if (orderA !== undefined || orderB !== undefined) {
            const orderResult = sorter({ order: orderA }, { order: orderB });
            if (orderResult !== 0) {
              return orderResult;
            }
          }

          const labelA = optionA ? getOptionLabel(optionA) : '';
          const labelB = optionB ? getOptionLabel(optionB) : '';

          if (!labelA) return 1;
          if (!labelB) return -1;

          return collator.compare(labelA, labelB);
        },

        meta: {
          ...columnDef.meta,
          columnType,
          name: columnDef.header,
          globalFilterFn: selectFilter.globalFilterFn,
          filter: selectFilter.meta,
          getGlobalFilterCondition: columnDef.getGlobalFilterCondition,
          getColumnFilterCondition: columnDef.getColumnFilterCondition,
          getColumnSort: columnDef.getColumnSort,
        },
      };
    };
}
